import { forwardRef, useRef } from 'react';
import { useAutocomplete } from '@mui/base';
import { UseAutocompleteProps } from '@mui/base/useAutocomplete';
import { Input } from '@mui/base/Input';
import { Popper } from '@mui/base/Popper';
import { unstable_useForkRef as useForkRef } from '@mui/utils';
import cx from 'classnames';
import s from './styles.scss';
import { Icon, enums, utils as u } from 'edrolo-ui';

export interface Option {
  label: string;
  value: string | number;
}

interface Props extends UseAutocompleteProps<Option, false, false, false> {
  placeholder?: string;
  isOptionEqualToValue?: (option: Option, value: Option) => boolean;
}

export const Autocomplete = forwardRef(function Autocomplete(
  props: Props,
  ref
) {
  const {
    disableClearable = false,
    disabled = false,
    readOnly = false,
    options,
    placeholder,
    isOptionEqualToValue = (option: Option, value: Option) =>
      option.value === value.value,
    ...other
  } = props;

  const {
    getRootProps,
    getInputProps,
    getPopupIndicatorProps,
    getClearProps,
    getListboxProps,
    getOptionProps,
    dirty,
    id,
    popupOpen,
    focused,
    anchorEl,
    setAnchorEl,
    groupedOptions,
  } = useAutocomplete({
    ...other,
    options,
    isOptionEqualToValue,
    componentName: 'Autocomplete',
  });

  const hasClearIcon = !disableClearable && !disabled && dirty && !readOnly;
  const containerRef = useRef(null);
  const rootRef = useForkRef(containerRef, setAnchorEl);

  return (
    <div
      className={cx(s.autocomplete, { [s.disabled]: disabled })}
      ref={containerRef}
    >
      <div
        {...getRootProps(other)}
        ref={rootRef}
        className={cx(s.inputWrapper, focused && s.focused)}
      >
        <div className={cx(u.flex, u.alignCenter, u.justifyCenter, u.ml1)}>
          <Icon name={enums.Icons.SEARCH} />
        </div>
        <Input
          id={id}
          ref={setAnchorEl}
          disabled={disabled}
          readOnly={readOnly}
          placeholder={placeholder}
          slotProps={{
            root: {
              className: s.inputRoot,
            },
            input: {
              className: s.input,
              ...getInputProps(),
            },
          }}
        />
        {hasClearIcon && (
          <button {...getClearProps()}>
            <Icon name={enums.Icons.CROSS} />
          </button>
        )}

        <button {...getPopupIndicatorProps()}>
          <Icon name={enums.Icons[popupOpen ? 'CHEVRON_UP' : 'CHEVRON_DOWN']} />
        </button>
      </div>
      {anchorEl && (
        <Popper
          open={popupOpen}
          anchorEl={anchorEl}
          slotProps={{
            root: { className: s.popper },
          }}
          container={containerRef.current}
        >
          <ul {...getListboxProps()} className={s.listBox}>
            {(groupedOptions as Option[]).map((option, index) => {
              const optionProps = getOptionProps({ option, index });

              return (
                <li key={index} {...optionProps}>
                  {option.label}
                </li>
              );
            })}

            {groupedOptions.length === 0 && <li>No results</li>}
          </ul>
        </Popper>
      )}
    </div>
  );
});
